import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { guideForSlot, docSlug, PREP_FOR_GUIDE } from "../lib/documentGuides";
import { ArrowLeft, ArrowRight, Loader2, Upload, Check, Trash2, Download, Lock, Send, ExternalLink, Wrench, ShieldCheck, Handshake, Info } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { FunderLogo, TIER_STYLE, ticketLabel } from "./FounderApply";

const NAV = [
  { to: "/founder/dashboard", label: "Dashboard" },
  { to: "/founder/apply", label: "Funder matches" },
  { to: "/founder/support", label: "Support" },
];
const BTN = "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold disabled:opacity-50";
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function FunderHeader({ f }) {
  const site = f.website ? (f.website.startsWith("http") ? f.website : `https://${f.website}`) : null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="funder-page-header">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <FunderLogo m={f} size="h-20 w-20 text-3xl" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight text-[#00303F] sm:text-3xl" data-testid="funder-page-name">{f.name}</h1>
            {f.tier && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${TIER_STYLE[f.tier]}`} data-testid="funder-page-tier">{f.tier === "Not yet" ? "Not yet" : `${f.tier} match`}</span>}
            {f.apply_route === "Via Clinq" && <span className="rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]">Featured partner</span>}
          </div>
          <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-400">{f.category}{f.instruments?.length ? ` · ${f.instruments.join(" / ")}` : ""}</p>
          <p className="mt-3 text-sm text-slate-700" data-testid="funder-page-description">
            {f.description || (f.sector_agnostic ? "Sector-agnostic — backs strong businesses across industries." : `Funds businesses in ${f.sectors_label}.`)}
          </p>
          <dl className="mt-4 grid gap-4 text-xs sm:grid-cols-3">
            <div><dt className="font-bold uppercase tracking-wide text-slate-400">What they fund</dt><dd className="mt-0.5 text-slate-700">{f.sectors_label || "Sector-agnostic"}</dd></div>
            <div><dt className="font-bold uppercase tracking-wide text-slate-400">Ticket size</dt><dd className="mt-0.5 font-bold text-[#00303F]" data-testid="funder-page-ticket">{ticketLabel(f)}</dd></div>
            <div><dt className="font-bold uppercase tracking-wide text-slate-400">Deploys in</dt><dd className="mt-0.5 text-slate-700">{f.deploys_in || "Africa"}</dd></div>
          </dl>
          {f.notes?.length ? <p className="mt-3 text-[11px] text-slate-500">{f.notes.join(" · ")}</p> : null}
          {site && <a href={site} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#0A8B85] hover:underline" data-testid="funder-page-website">Visit website <ExternalLink size={12} /></a>}
        </div>
      </div>
    </div>
  );
}

function DocGuideLink({ slotId }) {
  const guide = guideForSlot(slotId);
  if (!guide) {
    return (
      <span data-testid={`doc-guide-soon-${slotId}`}
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
        <Info className="h-3 w-3" /> Guide coming soon
      </span>
    );
  }
  const href = `${window.location.origin}/why-documents-matter#${docSlug(guide.title)}`;
  const prep = PREP_FOR_GUIDE[guide.title];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`doc-guide-link-${slotId}`} onClick={(e) => e.stopPropagation()}
          title="What is this document and why do funders ask for it?"
          className="ml-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-[#0A8B85] hover:bg-[#0A8B85]/10 hover:underline">
          <Info className="h-3.5 w-3.5" /> What's this?
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" onClick={(e) => e.stopPropagation()} data-testid={`doc-guide-popover-${slotId}`}
        className="w-[min(92vw,380px)] rounded-2xl border-[#0A8B85]/20 p-4 text-left shadow-xl">
        <p className="text-sm font-bold text-[#00303F]">{guide.title}</p>
        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#0A8B85]">What it is</p>
        <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{guide.what}</p>
        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#0A8B85]">Why funders want it</p>
        <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{guide.funders}</p>
        {prep && (
          <a href={prep.url} target="_blank" rel="noopener noreferrer" data-testid={`doc-guide-prep-${slotId}`}
            className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#0A8B85]/25 bg-[#0A8B85]/5 px-3 py-2.5 text-xs font-semibold text-[#00303F] transition-colors hover:bg-[#0A8B85]/10">
            <span className="inline-flex items-start gap-2"><Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0A8B85]" />{prep.verb} on Clinq Prep, then come back here and apply.</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[#0A8B85]" />
          </a>
        )}
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`doc-guide-full-${slotId}`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0A8B85] hover:underline">
          Read the full guide <ExternalLink className="h-3 w-3" />
        </a>
      </PopoverContent>
    </Popover>
  );
}

function DocSlot({ slot, doc, canUpload, matchId, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("slot", slot.id);
    setBusy(true);
    try {
      const { data } = await api.post(`/founder/funders/${matchId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data); toast.success(`${slot.label} uploaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/founder/funders/${matchId}/documents/${doc.doc_id}`); onChange(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove"); }
    finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const r = await api.get(`/founder/funders/${matchId}/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className={`rounded-xl border p-4 ${doc ? "border-emerald-200 bg-emerald-50/40" : "border-dashed border-slate-300 bg-white"}`} data-testid={`doc-slot-${slot.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${doc ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>{doc ? <Check size={16} /> : <Upload size={15} />}</span>
          <div>
            <p className="flex flex-wrap items-center gap-x-1 text-sm font-bold text-[#00303F]">{slot.label}<DocGuideLink slotId={slot.id} />{!slot.required && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Optional</span>}</p>
            {doc ? <p className="text-xs text-slate-600" data-testid={`doc-slot-${slot.id}-file`}>{doc.filename} · {kb(doc.size)}</p>
              : <p className="text-xs text-slate-500">PDF, PowerPoint, Word, Excel or image · up to 50MB</p>}
            {slot.note && !doc && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`doc-slot-${slot.id}-note`}>{slot.note}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {doc && <button type="button" onClick={download} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-[#0A8B85] hover:text-[#0A8B85]" title="Download" data-testid={`doc-slot-${slot.id}-download`}><Download size={15} /></button>}
          {doc && canUpload && <button type="button" onClick={remove} disabled={busy} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-red-300 hover:text-red-600" title="Remove" data-testid={`doc-slot-${slot.id}-remove`}><Trash2 size={15} /></button>}
          {canUpload && (
            <label className={`${BTN} cursor-pointer ${doc ? "border border-slate-200 bg-white text-[#00303F] hover:border-[#0A8B85]" : "bg-[#0A8B85] text-white hover:bg-[#097a74]"} ${busy ? "pointer-events-none opacity-50" : ""}`}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}{doc ? "Replace" : "Upload"}
              <input ref={inputRef} type="file" className="hidden" accept=".pdf,.pptx,.xlsx,.xlsm,.csv,.docx,.txt,.jpg,.jpeg,.png,.heic,.heif" onChange={(e) => upload(e.target.files?.[0])} data-testid={`doc-slot-${slot.id}-input`} />
            </label>
          )}
          {!canUpload && <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-400"><Lock size={12} /> Submitted</span>}
        </div>
      </div>
    </div>
  );
}

function useResubmitCheckout(matchId) {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/founder/funders/${matchId}/resubmit/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start payment"); setBusy(false); }
  };
  return { pay, busy };
}

const COUNTDOWN_SECONDS = 3 * 60 + 30;
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function ScoringCountdown({ funderName, startedAt }) {
  const started = startedAt ? new Date(startedAt).getTime() : Date.now();
  const [left, setLeft] = useState(() => Math.max(0, COUNTDOWN_SECONDS - Math.floor((Date.now() - started) / 1000)));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, COUNTDOWN_SECONDS - Math.floor((Date.now() - started) / 1000))), 1000);
    return () => clearInterval(t);
  }, [started]);
  const pct = Math.round(((COUNTDOWN_SECONDS - left) / COUNTDOWN_SECONDS) * 100);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#00303F]/70 px-4 backdrop-blur-sm" data-testid="scoring-countdown">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#0A8B85]/10">
          <span className="text-3xl font-extrabold tabular-nums text-[#0A8B85]" data-testid="scoring-countdown-time">{left > 0 ? mmss(left) : "…"}</span>
        </div>
        <h2 className="mt-5 text-xl font-extrabold text-[#00303F]">Reading your documents to see whether you qualify</h2>
        <p className="mt-2 text-sm text-slate-600">
          {left > 0 ? <>We're checking your pack against <b>{funderName}</b>'s requirements. Your result will appear here — and we'll also email it to you, so it's safe to leave this page.</>
            : "Almost there — finishing the last checks. Thanks for your patience."}
        </p>
        <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#0A8B85] transition-[width] duration-1000" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        <p className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> Clinq Capital Readiness engine</p>
      </div>
    </div>
  );
}

function ScorePanel({ matchId, st, setSt }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const resub = useResubmitCheckout(matchId);
  const sc = st.scoring;
  useEffect(() => {
    if (!sc || sc.status !== "running") return;
    const t = setInterval(() => api.get(`/founder/funders/${matchId}`).then(({ data }) => setSt(data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [sc?.status, matchId, setSt]);
  if (!sc) return null;

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/founder/funders/${matchId}/connect`); setSt(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the request"); }
    finally { setBusy(false); }
  };
  const rescore = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/founder/funders/${matchId}/rescore`); setSt(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't retry"); }
    finally { setBusy(false); }
  };

  if (sc.status === "running") {
    return <ScoringCountdown funderName={st.funder.name} startedAt={sc.started_at} />;
  }
  if (sc.status === "failed") {
    return (
      <section className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="score-failed">
        <p className="text-sm text-amber-800">We couldn't score your documents automatically. Please try again.</p>
        <button type="button" onClick={rescore} disabled={busy} className={`${BTN} bg-[#00303F] text-white`} data-testid="score-retry">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Retry scoring</button>
      </section>
    );
  }
  const strong = sc.outcome === "strong";
  const connected = !!st.application.connection_requested_at;
  return (
    <section className={`rounded-2xl border p-6 shadow-sm ${strong ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`} data-testid="score-result">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className={`flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-2xl ${strong ? "bg-emerald-600" : "bg-[#00303F]"} text-white`} data-testid="score-value">
          <span className="text-3xl font-extrabold leading-none">{sc.score}</span>
          <span className="mt-1 text-[10px] font-bold uppercase tracking-wide opacity-80">CRS / 100</span>
        </div>
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Fit score for {st.funder.name} · they need {sc.min_crs}+</p>
          <h2 className="mt-1 text-xl font-extrabold text-[#00303F]" data-testid="score-outcome">{strong ? "You are a strong candidate for this funder." : "You are not quite there yet."}</h2>
          {strong ? (
            connected ? (
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4" data-testid="connect-confirmation">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Check size={16} /></span>
                <div>
                  <p className="text-sm font-extrabold text-[#00303F]">We have connected you to this funder.</p>
                  <p className="text-xs text-slate-600">Our team will be in touch within two business days.</p>
                </div>
              </div>
            ) : (
              <>
                {sc.summary && <p className="mt-2 text-sm text-slate-600">{sc.summary}</p>}
                <button type="button" onClick={connect} disabled={busy} className={`${BTN} mt-4 bg-emerald-600 text-white hover:bg-emerald-700`} data-testid="connect-funder">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Handshake size={15} />} Connect me to this funder
                </button>
              </>
            )
          ) : (
            <>
              {sc.gaps?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Gaps identified in your documents</p>
                  <ul className="mt-2 space-y-2" data-testid="score-gaps">
                    {sc.gaps.map((g, i) => (
                      <li key={i} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`score-gap-${i}`}>
                        <p className="text-sm font-bold text-[#00303F]">{g.text}</p>
                        {g.action && <p className="mt-0.5 text-xs text-slate-600"><span className="font-bold text-[#0A8B85]">Fix:</span> {g.action}</p>}
                        {g.tool?.url && (
                          <a href={g.tool.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85]/10 px-2.5 py-1.5 text-xs font-bold text-[#0A8B85] hover:bg-[#0A8B85]/20" data-testid={`score-gap-${i}-tool`}>
                            <Wrench size={12} /> Build it in Clinq Prep → {g.tool.label} <ExternalLink size={11} />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button type="button" onClick={() => nav("/onboarding")} className={`${BTN} bg-[#0A8B85] text-white hover:bg-[#097a74]`} data-testid="score-report-cta">
                  Get my Capital Readiness Report to see exactly what to improve <ArrowRight size={15} />
                </button>
                {st.can_upload ? (
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid="resubmit-unlocked-note"><ShieldCheck size={13} /> Resubmission unlocked — replace your documents below and submit again</p>
                ) : (
                  <button type="button" onClick={resub.pay} disabled={resub.busy} className={`${BTN} border border-slate-300 bg-white text-[#00303F] hover:border-[#0A8B85]`} data-testid="resubmit-pay">
                    {resub.busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Resubmit new documents — KES {st.resubmit_price_kes}
                  </button>
                )}
              </div>
              <p className="mt-2 text-[11px] text-slate-500" data-testid="score-cta-explainer">
                The report is a paid, full six-pillar assessment with your Relationship Manager (price shown on the next page). Resubmitting replaces any documents and gets you re-scored by this funder — one extra submission.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default function FounderFunderPage() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/founder/funders/${matchId}`).then(({ data }) => setSt(data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load this funder"));
  }, [matchId]);

  useEffect(() => {
    const ref = params.get("resubmit_ref");
    if (!ref) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.post(`/founder/funders/${matchId}/resubmit/poll/${ref}`);
        if (data.payment_status === "paid") { setSt(data); toast.success("Payment received — you can now upload your updated documents"); params.delete("resubmit_ref"); setParams(params, { replace: true }); return; }
        if (data.payment_status === "failed") { toast.error("Payment was not completed"); params.delete("resubmit_ref"); setParams(params, { replace: true }); return; }
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2500);
    };
    poll();
  }, [params.get("resubmit_ref")]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSubmitting(true);
    try { const { data } = await api.post(`/founder/funders/${matchId}/submit`); setSt(data); toast.success(`Documents submitted to ${data.funder.name}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't submit"); }
    finally { setSubmitting(false); }
  };

  const docBySlot = Object.fromEntries((st?.documents || []).map((d) => [d.slot, d]));
  const isDebt = st?.pack.id === "debt";

  return (
    <AppShell nav={NAV}>
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="founder-funder-page">
        <button type="button" onClick={() => nav("/founder/apply")} className="inline-flex items-center gap-1 text-xs font-bold text-[#0A8B85] hover:underline" data-testid="funder-page-back"><ArrowLeft size={14} /> Back to your matches</button>
        {!st && !err && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>}
        {err && <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600" data-testid="funder-page-error">{err}</div>}
        {st && (
          <div className="mt-4 space-y-6">
            <FunderHeader f={st.funder} />
            {st.application.status === "submitted" && <ScorePanel matchId={matchId} st={st} setSt={setSt} />}

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="doc-upload-section">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">{st.pack.label}</p>
                  <h2 className="mt-1 text-lg font-extrabold text-[#00303F]">Documents {st.funder.name} will ask for</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    {isDebt ? "Lenders assess repayment capacity — statements, accounts and registration come first. The two optional extras below lift your score if you have them." : "Investors look for the story, the numbers and proof the company exists."}
                    {st.application.submission_count === 0 ? " Your first submission is free." : ""}
                  </p>
                  {st.can_upload && <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600" data-testid="uploads-saved-note"><ShieldCheck size={12} className="text-[#0A8B85]" /> Each upload is saved instantly — you can leave and come back later to finish.</p>}
                </div>
                {st.application.resubmission_open && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-700" data-testid="resubmission-open-badge"><ShieldCheck size={12} /> Resubmission unlocked</span>}
              </div>
              <div className="mt-5 space-y-3">
                {st.pack.slots.map((s) => <DocSlot key={s.id} slot={s} doc={docBySlot[s.id]} canUpload={st.can_upload} matchId={matchId} onChange={setSt} />)}
              </div>
              {st.can_upload ? (
                <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-slate-500">{st.can_submit ? "All required documents are in — send them when you're ready." : "Upload every required document to submit."}</p>
                  <button type="button" onClick={submit} disabled={!st.can_submit || submitting} className={`${BTN} bg-[#0A8B85] text-white hover:bg-[#097a74]`} data-testid="submit-application">
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}{st.application.submission_count > 0 ? "Submit updated documents" : "Submit documents"}
                  </button>
                </div>
              ) : (
                <p className="mt-5 text-xs text-slate-500" data-testid="submitted-note">
                  Submitted to {st.funder.name} on {new Date(st.application.last_submitted_at).toLocaleDateString()}
                  {st.application.submission_count > 1 ? ` (submission #${st.application.submission_count})` : ""}.
                </p>
              )}
            </section>

            <a href={st.prep_url} target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-start gap-3 rounded-2xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 p-5 transition-colors hover:bg-[#0A8B85]/10 sm:flex-row sm:items-center sm:justify-between" data-testid="build-docs-prompt">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#0A8B85] shadow-sm"><Wrench size={18} /></span>
                <div>
                  <p className="text-sm font-extrabold text-[#00303F]">Don't have this documentation? You can build it here</p>
                  <p className="text-xs text-slate-500">Get match-ready in Clinq Prep — build a pitch deck, financial model or investor-ready accounts, then come back here to upload it{st.application.submission_count > 0 ? " and resubmit" : ""}.</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0A8B85]">prep.clinq.africa <ExternalLink size={14} /></span>
            </a>
          </div>
        )}
      </div>
    </AppShell>
  );
}
